import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLiveKitRoom } from '../hooks/useLiveKitRoom';
import { VideoTile } from './VideoTile';
import { AudioControls } from './AudioControls';
import { TrackSelector } from './TrackSelector';
import { InstantReplay } from './InstantReplay';

export function ObserverView({ client, onLogout }) {
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [rooms, setRooms] = useState([]);
  const [selectedTracks, setSelectedTracks] = useState(new Set());
  const [audioVolumes, setAudioVolumes] = useState(new Map());
  // User-defined display order of video tiles (array of track ids). Tiles can be
  // dragged to reorder; new tracks append, removed tracks fall out.
  const [tileOrder, setTileOrder] = useState([]);
  const [draggingId, setDraggingId] = useState(null);
  const dragIdRef = useRef(null);

  const {
    room,
    connectionState,
    participants,
    videoTracks,
    audioTracks,
    error,
    connect,
    disconnect,
  } = useLiveKitRoom(client, selectedRoomId);

  // Get Matrix rooms that might have calls
  useEffect(() => {
    if (!client) return;

    const updateRooms = () => {
      const allRooms = client.getRooms();
      // Filter rooms - show all joined rooms
      const joinedRooms = allRooms.filter(r => r.getMyMembership() === 'join');
      setRooms(joinedRooms);
    };

    updateRooms();

    // Listen for room changes
    client.on('Room.myMembership', updateRooms);
    client.on('sync', updateRooms);

    return () => {
      client.off('Room.myMembership', updateRooms);
      client.off('sync', updateRooms);
    };
  }, [client]);

  // Initialize all tracks as selected when they're discovered
  useEffect(() => {
    const allTrackIds = new Set([
      ...Array.from(videoTracks.keys()),
      ...Array.from(audioTracks.keys()),
    ]);

    // Add new tracks to selection
    setSelectedTracks(prev => {
      const next = new Set(prev);
      allTrackIds.forEach(id => {
        if (!next.has(id)) {
          next.add(id);
        }
      });
      return next;
    });
  }, [videoTracks, audioTracks]);

  // Handle track visibility toggle
  const handleTrackToggle = useCallback((trackId) => {
    setSelectedTracks(prev => {
      const next = new Set(prev);
      if (next.has(trackId)) {
        next.delete(trackId);
      } else {
        next.add(trackId);
      }
      return next;
    });
  }, []);

  // Handle volume change
  const handleVolumeChange = useCallback((trackId, volume) => {
    setAudioVolumes(prev => {
      const next = new Map(prev);
      next.set(trackId, volume);
      return next;
    });
  }, []);

  // Filter visible video tracks
  const visibleVideoTracks = useMemo(() => {
    return Array.from(videoTracks.entries())
      .filter(([id]) => selectedTracks.has(id));
  }, [videoTracks, selectedTracks]);

  // Filter visible audio tracks
  const visibleAudioTracks = useMemo(() => {
    return Array.from(audioTracks.entries())
      .filter(([id]) => selectedTracks.has(id));
  }, [audioTracks, selectedTracks]);

  // Keep tileOrder in sync with the available video tracks: append new ids,
  // drop ids whose track is gone (preserving the user's existing order).
  useEffect(() => {
    const ids = Array.from(videoTracks.keys());
    setTileOrder(prev => {
      const present = new Set(ids);
      const kept = prev.filter(id => present.has(id));
      const added = ids.filter(id => !kept.includes(id));
      const next = [...kept, ...added];
      // Avoid a state update (and re-render loop) if nothing changed.
      if (next.length === prev.length && next.every((id, i) => id === prev[i])) {
        return prev;
      }
      return next;
    });
  }, [videoTracks]);

  // Visible video tiles in the user's chosen order.
  const orderedVideoTracks = useMemo(() => {
    const rank = new Map(tileOrder.map((id, i) => [id, i]));
    return [...visibleVideoTracks].sort(
      ([a], [b]) => (rank.get(a) ?? Infinity) - (rank.get(b) ?? Infinity)
    );
  }, [visibleVideoTracks, tileOrder]);

  // --- Drag-and-drop tile reordering ---
  const handleTileDragStart = useCallback((id) => {
    dragIdRef.current = id;
    setDraggingId(id);
  }, []);

  const handleTileDragEnd = useCallback(() => {
    dragIdRef.current = null;
    setDraggingId(null);
  }, []);

  const handleTileDrop = useCallback((targetId) => {
    const sourceId = dragIdRef.current;
    if (!sourceId || sourceId === targetId) return;
    setTileOrder(prev => {
      const next = prev.filter(id => id !== sourceId);
      const targetIdx = next.indexOf(targetId);
      if (targetIdx === -1) return prev;
      next.splice(targetIdx, 0, sourceId);
      return next;
    });
  }, []);

  const handleConnect = async () => {
    if (selectedRoomId) {
      await connect();
    }
  };

  const handleDisconnect = async () => {
    await disconnect();
  };

  const userId = client?.getUserId() || '';

  return (
    <div className="observer-view">
      <header className="header">
        <h1>LiveKit Observer</h1>
        <div className="header-controls">
          <span className={`status-badge ${connectionState}`}>
            {connectionState}
          </span>
          <div className="user-info">
            <span>{userId}</span>
          </div>
          <button className="btn btn-secondary" onClick={onLogout}>
            Sign Out
          </button>
        </div>
      </header>

      <div className="main-layout">
        <div className="sidebar">
          {/* Room Selector */}
          <div className="panel room-selector">
            <h2>Room</h2>
            <select
              value={selectedRoomId}
              onChange={(e) => setSelectedRoomId(e.target.value)}
              disabled={connectionState === 'connected'}
            >
              <option value="">Select a room...</option>
              {rooms.map(r => (
                <option key={r.roomId} value={r.roomId}>
                  {r.name || r.roomId}
                </option>
              ))}
            </select>
            <div className="room-actions">
              {connectionState === 'disconnected' ? (
                <button
                  className="btn btn-primary"
                  onClick={handleConnect}
                  disabled={!selectedRoomId}
                >
                  Connect
                </button>
              ) : (
                <button
                  className="btn btn-danger"
                  onClick={handleDisconnect}
                >
                  Disconnect
                </button>
              )}
            </div>
            {error && <div className="error-message">{error}</div>}
          </div>

          {/* Track Selector */}
          {connectionState === 'connected' && (
            <TrackSelector
              videoTracks={videoTracks}
              audioTracks={audioTracks}
              selectedTracks={selectedTracks}
              onToggle={handleTrackToggle}
            />
          )}

          {/* Audio Controls */}
          {connectionState === 'connected' && visibleAudioTracks.length > 0 && (
            <AudioControls
              audioTracks={visibleAudioTracks}
              volumes={audioVolumes}
              onVolumeChange={handleVolumeChange}
            />
          )}

          {/* Instant Replay */}
          {connectionState === 'connected' && (
            <InstantReplay
              videoTracks={visibleVideoTracks}
              audioTracks={visibleAudioTracks}
              matrixRoomId={selectedRoomId}
              client={client}
            />
          )}
        </div>

        <div className="content">
          {connectionState === 'connected' ? (
            <div
              className="video-grid"
              data-tiles={Math.min(visibleVideoTracks.length, 36)}
            >
              {orderedVideoTracks.length > 0 ? (
                orderedVideoTracks.map(([id, { track, publication, participant }]) => (
                  <VideoTile
                    key={id}
                    id={id}
                    track={track}
                    publication={publication}
                    participant={participant}
                    isDragging={draggingId === id}
                    onTileDragStart={handleTileDragStart}
                    onTileDragEnd={handleTileDragEnd}
                    onTileDrop={handleTileDrop}
                  />
                ))
              ) : (
                <div className="empty-state">
                  <p>No video tracks available</p>
                  <p>Waiting for participants to share video...</p>
                </div>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <p>Select a room and connect to start observing</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
